// react and js
import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect } from "react";
import { useFormik, FormikProvider } from "formik";
import PropTypes from "prop-types";

// mui
import { Stack, Box, IconButton, DialogTitle, DialogContent, DialogActions, Typography, FormControl, Select, Divider } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";
import { SmartNumberInput } from "../../../components/SmartInputs";

// theme
import {
  B1Tiny,
  ModalActionsLoadingButton,
  ModalContentOutlinedInput,
  ModalContentMenuItem,
  ModalContentSwitch,
  ModalContentTextField,
} from "../../../theme/styled";

// ----------------------------------------------------------------------
// Constants
// ----------------------------------------------------------------------
const MAX_VALUE_RELATIONS = 6;
const MAX_TITLE_FILTERS = 50;

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const NewsDefaults = {
  nSignalInUse: false,
  nSignalUsedFor: "0",
  nSignalVisualization: false,
  nDrawTable: false,
  nDrawChart: true,
  nDrawKeepInCache: "20",
  nSignalNegation: false,
  nShiftInSec: "0",
  nPreSignalTime: "1800",
  nPostSignalTime: "1800",
  nRegionAll: false,
  nRegionAud: true,
  nRegionBrl: false,
  nRegionCad: true,
  nRegionChf: false,
  nRegionCny: false,
  nRegionEur: true,
  nRegionGbp: true,
  nRegionHkd: false,
  nRegionInr: false,
  nRegionJpy: false,
  nRegionKrw: false,
  nRegionMxn: false,
  nRegionNok: false,
  nRegionNzd: false,
  nRegionSek: false,
  nRegionSgd: false,
  nRegionUsd: true,
  nRegionZar: false,
  nImportanceUndefined: true,
  nImportanceLow: true,
  nImportanceModerate: true,
  nImportanceHigh: true,
  nImpactUndefined: true,
  nImpactPositive: true,
  nImpactNegative: true,
  bActual: false,
  lActualMin: "-100000000",
  lActualMax: "100000000",
  bForecast: false,
  lForecastMin: "-100000000",
  lForecastMax: "100000000",
  bPrevious: false,
  lPreviousMin: "-100000000",
  lPreviousMax: "100000000",
};

export const NewsValueRelationsDefaults = {
  nRelationCondition: "0",
}

export const NewsTitleFiltersDefaults = {
  nFilterType: "0",
  strFilterString: "Title or part of title ...",
}

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const DEFAULT_LIMITS = {
  nDrawKeepInCache: {
    min: 0,
  },
  nShiftInSec: {},
  nPreSignalTime: {
    min: 0,
  },
  nPostSignalTime: {
    min: 0,
  },
  lActualMin: {},
  lActualMax: {},
  lForecastMin: {},
  lForecastMax: {},
  lPreviousMin: {},
  lPreviousMax: {},
  strFilterString: {
    maxLength: 60,
    emptyAllowed: false,
    defaultValue: "",
    disallowedPattern: /[<>{}[\]\\|`~@#$%^&*+=]/,
  },
};

// ----------------------------------------------------------------------
// Filter Type Options
// ----------------------------------------------------------------------
const FILTER_TYPE_OPTIONS = [
  { value: "0", label: "Off" },
  { value: "1", label: "Exclude" },
  { value: "2", label: "Exclusive" },
];

// ----------------------------------------------------------------------
// Relation Condition Options
// ----------------------------------------------------------------------
const RELATION_CONDITION_OPTIONS = [
  { value: "0", label: "Off" },
  { value: "1", label: "Actual greater forecast" },
  { value: "2", label: "Actual greater previous" },
  { value: "3", label: "Actual smaller forecast" },
  { value: "4", label: "Actual smaller previous" },
  { value: "5", label: "Forecast greater previous" },
  { value: "6", label: "Forecast smaller previous" },
];

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export function NewsRenderParameters(professionalMode, config) {
  const renderParam = (label, value, leftWidth = "auto", rightWidth = "full") => (
    <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
      <Typography
        variant="contentTiny"
        sx={{
          fontWeight: 600,
          flex: `1 1 ${leftWidth}`,
          maxWidth: leftWidth,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="contentTiny"
        sx={{
          flex: `1 1 ${rightWidth}`,
          maxWidth: rightWidth,
          wordBreak: "break-word",
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  // Helper function to get display values
  const getDisplayValue = (key) => config[key];

  // Helper function to get switch display values (On/Off)
  const getSwitchDisplay = (value) => (value === true ? "On" : "Off");

  // Helper function to get signal used for display
  const getSignalUsedForDisplay = (value) => {
    switch (value) {
      case "0":
        return "Entry";
      case "1":
        return "Exit";
      default:
        return "Not set";
    }
  };

  // Helper function to render a divider
  const renderDivider = () => <Divider sx={{ my: 1, borderColor: "#C8C8C8" }} />;

  return (
    <Stack spacing={0.5}>
      {/* Basic signal configuration */}
      {renderParam("Signal in use", getSwitchDisplay(config.nSignalInUse))}
      {renderParam("Signal used for", getSignalUsedForDisplay(config.nSignalUsedFor))}
      {renderParam("Signal visualization", getSwitchDisplay(config.nSignalVisualization))}

      {renderDivider()}

      {/* Drawing configuration */}
      {renderParam("Draw table with upcomming events", getSwitchDisplay(config.nDrawTable))}
      {renderParam("Draw signal lines with name in chart", getSwitchDisplay(config.nDrawChart))}
      {renderParam("Cache size for kept signal lines", getDisplayValue("nDrawKeepInCache"))}

      {renderDivider()}

      {/* Signal timing configuration */}
      {renderParam("Signal inversion", getSwitchDisplay(config.nSignalNegation))}
      {renderParam("Event shift in seconds", getDisplayValue("nShiftInSec"))}
      {renderParam("Pre event signal time", getDisplayValue("nPreSignalTime"))}
      {renderParam("Post event signal time", getDisplayValue("nPostSignalTime"))}

      {renderDivider()}

      {/* Region switches - show only when enabled */}
      {config.nRegionAll === true ? renderParam("Signals for ALL regions", getSwitchDisplay(config.nRegionAll)) : null}
      {config.nRegionAud === true ? renderParam("Signals for AUD region", getSwitchDisplay(config.nRegionAud)) : null}
      {config.nRegionBrl === true ? renderParam("Signals for BRL region", getSwitchDisplay(config.nRegionBrl)) : null}
      {config.nRegionCad === true ? renderParam("Signals for CAD region", getSwitchDisplay(config.nRegionCad)) : null}
      {config.nRegionChf === true ? renderParam("Signals for CHF region", getSwitchDisplay(config.nRegionChf)) : null}
      {config.nRegionCny === true ? renderParam("Signals for CNY region", getSwitchDisplay(config.nRegionCny)) : null}
      {config.nRegionEur === true ? renderParam("Signals for EUR region", getSwitchDisplay(config.nRegionEur)) : null}
      {config.nRegionGbp === true ? renderParam("Signals for GBP region", getSwitchDisplay(config.nRegionGbp)) : null}
      {config.nRegionHkd === true ? renderParam("Signals for HKD region", getSwitchDisplay(config.nRegionHkd)) : null}
      {config.nRegionInr === true ? renderParam("Signals for INR region", getSwitchDisplay(config.nRegionInr)) : null}
      {config.nRegionJpy === true ? renderParam("Signals for JPY region", getSwitchDisplay(config.nRegionJpy)) : null}
      {config.nRegionKrw === true ? renderParam("Signals for KRW region", getSwitchDisplay(config.nRegionKrw)) : null}
      {config.nRegionMxn === true ? renderParam("Signals for MXN region", getSwitchDisplay(config.nRegionMxn)) : null}
      {config.nRegionNok === true ? renderParam("Signals for NOK region", getSwitchDisplay(config.nRegionNok)) : null}
      {config.nRegionNzd === true ? renderParam("Signals for NZD region", getSwitchDisplay(config.nRegionNzd)) : null}
      {config.nRegionSek === true ? renderParam("Signals for SEK region", getSwitchDisplay(config.nRegionSek)) : null}
      {config.nRegionSgd === true ? renderParam("Signals for SGD region", getSwitchDisplay(config.nRegionSgd)) : null}
      {config.nRegionUsd === true ? renderParam("Signals for USD region", getSwitchDisplay(config.nRegionUsd)) : null}
      {config.nRegionZar === true ? renderParam("Signals for ZAR region", getSwitchDisplay(config.nRegionZar)) : null}

      {renderDivider()}

      {/* Importance switches - show all */}
      {renderParam("Signals for undefined importance", getSwitchDisplay(config.nImportanceUndefined))}
      {renderParam("Signals for low importance", getSwitchDisplay(config.nImportanceLow))}
      {renderParam("Signals for moderate importance", getSwitchDisplay(config.nImportanceModerate))}
      {renderParam("Signals for high importance", getSwitchDisplay(config.nImportanceHigh))}

      {renderDivider()}

      {/* Impact switches - show all */}
      {renderParam("Signals for undefined impact", getSwitchDisplay(config.nImpactUndefined))}
      {renderParam("Signals for positive impact", getSwitchDisplay(config.nImpactPositive))}
      {renderParam("Signals for negative impact", getSwitchDisplay(config.nImpactNegative))}

      {renderDivider()}

      {/* Conditional range values - only show min/max when enabled */}
      {renderParam("Actual value validation", getSwitchDisplay(config.bActual))}
      {config.bActual === true ? (
        <>
          {renderParam("Actual value minimum", getDisplayValue("lActualMin"))}
          {renderParam("Actual value maximum", getDisplayValue("lActualMax"))}
        </>
      ) : null}

      {renderDivider()}

      {renderParam("Forecast value validation", getSwitchDisplay(config.bForecast))}
      {config.bForecast === true ? (
        <>
          {renderParam("Forecast value minimum", getDisplayValue("lForecastMin"))}
          {renderParam("Forecast value maximum", getDisplayValue("lForecastMax"))}
        </>
      ) : null}

      {renderDivider()}

      {renderParam("Previous value validation", getSwitchDisplay(config.bPrevious))}
      {config.bPrevious === true ? (
        <>
          {renderParam("Previous value minimum", getDisplayValue("lPreviousMin"))}
          {renderParam("Previous value maximum", getDisplayValue("lPreviousMax"))}
        </>
      ) : null}

      {(() => {
        // Parse value relations
        let relations = [];

        // Check for new array format first
        if (config.valueRelations && Array.isArray(config.valueRelations)) {
          relations = config.valueRelations.map((relationData) => {
            const relationCondition = relationData.nRelationCondition || "0";
            const relationLabel = RELATION_CONDITION_OPTIONS.find((opt) => opt.value === relationCondition)?.label || "Unknown";
            return {
              relationCondition,
              relationLabel,
            };
          });
        }

        return (
          <>
            {renderDivider()}
            <Typography variant="contentTiny" sx={{ fontWeight: 600 }}>
              Value relations ({relations.length}/{MAX_VALUE_RELATIONS})
            </Typography>
            {relations.map((relation, index) => (
              <Typography key={index} variant="contentTiny">
                {relation.relationLabel}
              </Typography>
            ))}
          </>
        );
      })()}

      {(() => {
        // Parse title filters
        let filters = [];

        // Check for new array format first
        if (config.titleFilters && Array.isArray(config.titleFilters)) {
          filters = config.titleFilters.map((filterData) => {
            const filterType = filterData.nFilterType || "0";
            const filterTypeLabel = FILTER_TYPE_OPTIONS.find((opt) => opt.value === filterType)?.label || "Unknown";
            return {
              filterType,
              filterTypeLabel,
              filterString: filterData.strFilterString || "",
            };
          });
        }

        return (
          <>
            {renderDivider()}
            <Typography variant="contentTiny" sx={{ fontWeight: 600 }}>
              Title filters ({filters.length}/{MAX_TITLE_FILTERS})
            </Typography>
            {filters.map((filter, index) => (
              <Typography key={index} variant="contentTiny">
                <span style={{ marginRight: 4, color: "#666" }}>{filter.filterTypeLabel}:</span>
                {filter.filterString}
              </Typography>
            ))}
          </>
        );
      })()}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export const NewsConfigure = forwardRef(({ onSave, initialData = null, professionalMode = null }, ref) => {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  NewsConfigure.propTypes = {
    onSave: PropTypes.func.isRequired,
    initialData: PropTypes.object,
    professionalMode: PropTypes.any,
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [scroll, setScroll] = useState("paper");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const autoCloseTimeoutRef = useRef(null);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    openDialog: () => {
      setOpen(true);
    },
    closeDialog: () => {
      setOpen(false);
    },
  }));

  // Parse initial title filters from initialData
  const parseInitialFilters = () => {
    const filters = [];
    if (initialData) {
      // Check for new array format first
      if (initialData.titleFilters && Array.isArray(initialData.titleFilters)) {
        initialData.titleFilters.forEach((filterData, index) => {
          filters.push({
            id: index + 1,
            filterType: filterData.nFilterType || "0",
            filterString: filterData.strFilterString || "",
          });
        });
      }
    }
    return filters.length > 0 ? filters : [];
  };

  // Parse initial value relations from initialData
  const parseInitialRelations = () => {
    const relations = [];
    if (initialData) {
      // Check for new array format first
      if (initialData.valueRelations && Array.isArray(initialData.valueRelations)) {
        initialData.valueRelations.forEach((relationData, index) => {
          relations.push({
            id: index + 1,
            relationCondition: relationData.nRelationCondition || "0",
          });
        });
      }
    }
    return relations.length > 0 ? relations : [];
  };

  const [valueRelations, setValueRelations] = useState(parseInitialRelations);
  const [titleFilters, setTitleFilters] = useState(parseInitialFilters);

  const addNewRelation = () => {
    if (valueRelations.length < MAX_VALUE_RELATIONS) {
      const newId = Math.max(...valueRelations.map((r) => parseInt(r.id, 10)), 0) + 1;
      setValueRelations([
        ...valueRelations,
        {
          id: newId.toString(),
          relationCondition: NewsValueRelationsDefaults.nRelationCondition,
        },
      ]);
    }
  };

  const removeRelation = (id) => {
    setValueRelations(valueRelations.filter((relation) => relation.id !== id));
  };

  const updateRelation = (id, field, value) => {
    setValueRelations(valueRelations.map((relation) => (relation.id === id ? { ...relation, [field]: value } : relation)));
  };

  const addNewFilter = () => {
    if (titleFilters.length < MAX_TITLE_FILTERS) {
      const newId = Math.max(...titleFilters.map((f) => parseInt(f.id, 10)), 0) + 1;
      setTitleFilters([
        ...titleFilters,
        {
          id: newId.toString(),
          filterType: NewsTitleFiltersDefaults.nFilterType,
          filterString: NewsTitleFiltersDefaults.strFilterString,
        },
      ]);
    }
  };

  const removeFilter = (id) => {
    setTitleFilters(titleFilters.filter((filter) => filter.id !== id));
  };

  const updateFilter = (id, field, value) => {
    // For text fields, keep all text (no filtering)
    if (field === "filterString") {
      // Update with original value (no filtering)
      setTitleFilters(titleFilters.map((filter) => (filter.id === id ? { ...filter, [field]: value } : filter)));
    } else {
      // For other fields, update directly
      setTitleFilters(titleFilters.map((filter) => (filter.id === id ? { ...filter, [field]: value } : filter)));
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleReset = () => {
    // Clear all errors and touched state
    setErrors({});
    setTouched({});
    // Clear cross-validation error state
    setError(false);
    setReturnMessage("");

    // Apply all the default values to the form
    Object.keys(NewsDefaults).forEach((key) => {
      setFieldValue(key, NewsDefaults[key]);
    });
    // Reset values in value relations array (preserve structure)
    setValueRelations(
      valueRelations.map((relation) => ({
        ...relation,
        relationCondition: NewsValueRelationsDefaults.nRelationCondition,
      }))
    );
    // Reset values in title filters array (preserve structure)
    setTitleFilters(
      titleFilters.map((filter) => ({
        ...filter,
        filterType: NewsTitleFiltersDefaults.nFilterType,
        filterString: NewsTitleFiltersDefaults.strFilterString,
      }))
    );
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClose = () => {
    // Clear any existing auto-close timeout
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    setOpen(false);
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");
    resetForm();
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const formik = useFormik({
    initialValues: {
      nSignalInUse: initialData?.nSignalInUse ?? NewsDefaults.nSignalInUse,
      nSignalUsedFor: initialData?.nSignalUsedFor ?? NewsDefaults.nSignalUsedFor,
      nSignalVisualization: initialData?.nSignalVisualization ?? NewsDefaults.nSignalVisualization,
      nDrawTable: initialData?.nDrawTable ?? NewsDefaults.nDrawTable,
      nDrawChart: initialData?.nDrawChart ?? NewsDefaults.nDrawChart,
      nDrawKeepInCache: initialData?.nDrawKeepInCache ?? NewsDefaults.nDrawKeepInCache,
      nSignalNegation: initialData?.nSignalNegation ?? NewsDefaults.nSignalNegation,
      nShiftInSec: initialData?.nShiftInSec ?? NewsDefaults.nShiftInSec,
      nPreSignalTime: initialData?.nPreSignalTime ?? NewsDefaults.nPreSignalTime,
      nPostSignalTime: initialData?.nPostSignalTime ?? NewsDefaults.nPostSignalTime,
      nRegionAll: initialData?.nRegionAll ?? NewsDefaults.nRegionAll,
      nRegionAud: initialData?.nRegionAud ?? NewsDefaults.nRegionAud,
      nRegionBrl: initialData?.nRegionBrl ?? NewsDefaults.nRegionBrl,
      nRegionCad: initialData?.nRegionCad ?? NewsDefaults.nRegionCad,
      nRegionChf: initialData?.nRegionChf ?? NewsDefaults.nRegionChf,
      nRegionCny: initialData?.nRegionCny ?? NewsDefaults.nRegionCny,
      nRegionEur: initialData?.nRegionEur ?? NewsDefaults.nRegionEur,
      nRegionGbp: initialData?.nRegionGbp ?? NewsDefaults.nRegionGbp,
      nRegionHkd: initialData?.nRegionHkd ?? NewsDefaults.nRegionHkd,
      nRegionInr: initialData?.nRegionInr ?? NewsDefaults.nRegionInr,
      nRegionJpy: initialData?.nRegionJpy ?? NewsDefaults.nRegionJpy,
      nRegionKrw: initialData?.nRegionKrw ?? NewsDefaults.nRegionKrw,
      nRegionMxn: initialData?.nRegionMxn ?? NewsDefaults.nRegionMxn,
      nRegionNok: initialData?.nRegionNok ?? NewsDefaults.nRegionNok,
      nRegionNzd: initialData?.nRegionNzd ?? NewsDefaults.nRegionNzd,
      nRegionSek: initialData?.nRegionSek ?? NewsDefaults.nRegionSek,
      nRegionSgd: initialData?.nRegionSgd ?? NewsDefaults.nRegionSgd,
      nRegionUsd: initialData?.nRegionUsd ?? NewsDefaults.nRegionUsd,
      nRegionZar: initialData?.nRegionZar ?? NewsDefaults.nRegionZar,
      nImportanceUndefined: initialData?.nImportanceUndefined ?? NewsDefaults.nImportanceUndefined,
      nImportanceLow: initialData?.nImportanceLow ?? NewsDefaults.nImportanceLow,
      nImportanceModerate: initialData?.nImportanceModerate ?? NewsDefaults.nImportanceModerate,
      nImportanceHigh: initialData?.nImportanceHigh ?? NewsDefaults.nImportanceHigh,
      nImpactUndefined: initialData?.nImpactUndefined ?? NewsDefaults.nImpactUndefined,
      nImpactPositive: initialData?.nImpactPositive ?? NewsDefaults.nImpactPositive,
      nImpactNegative: initialData?.nImpactNegative ?? NewsDefaults.nImpactNegative,
      bActual: initialData?.bActual ?? NewsDefaults.bActual,
      lActualMin: initialData?.lActualMin ?? NewsDefaults.lActualMin,
      lActualMax: initialData?.lActualMax ?? NewsDefaults.lActualMax,
      bForecast: initialData?.bForecast ?? NewsDefaults.bForecast,
      lForecastMin: initialData?.lForecastMin ?? NewsDefaults.lForecastMin,
      lForecastMax: initialData?.lForecastMax ?? NewsDefaults.lForecastMax,
      bPrevious: initialData?.bPrevious ?? NewsDefaults.bPrevious,
      lPreviousMin: initialData?.lPreviousMin ?? NewsDefaults.lPreviousMin,
      lPreviousMax: initialData?.lPreviousMax ?? NewsDefaults.lPreviousMax,
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { setSubmitting }) => {
      setTimeout(() => {
        try {
          // Check if any filter has empty filter string
          const hasEmptyFilterString = titleFilters.some((filter) => filter.filterString.trim() === "");

          if (hasEmptyFilterString) {
            setError(true);
            setReturnMessage("Filter string cannot be empty in any title filter.");
            setSubmitting(false);

            // Clear error message after 5 seconds
            setTimeout(() => {
              setError(false);
              setReturnMessage("");
            }, 5000);

            return;
          }

          // Check for validation errors in filters
          const validationErrors = getFilterValidationErrors();
          if (Object.keys(validationErrors).length > 0) {
            const errorMessages = [];
            Object.values(validationErrors).forEach((errors) => {
              Object.values(errors).forEach((error) => {
                if (!errorMessages.includes(error)) {
                  errorMessages.push(error);
                }
              });
            });

            setError(true);
            setReturnMessage(`Validation errors: ${errorMessages.join(", ")}`);
            setSubmitting(false);

            // Clear error message after 5 seconds
            setTimeout(() => {
              setError(false);
              setReturnMessage("");
            }, 5000);

            return;
          }

          // Format arrays - only include valid items
          const formattedValues = { ...values };
          const validFilters = titleFilters.filter((filter) => filter.filterString.trim() !== "");
          const validRelations = valueRelations.filter((relation) => relation.relationCondition !== "0");

          // Create value relations array
          formattedValues.valueRelations = validRelations.map((relation) => ({
            nRelationCondition: relation.relationCondition,
          }));

          // Create title filters array
          formattedValues.titleFilters = validFilters.map((filter) => ({
            nFilterType: filter.filterType,
            strFilterString: filter.filterString,
          }));

          onSave(formattedValues);
          setSuccess(true);
          setReturnMessage("Configuration saved successfully!");
          // autoCloseTimeoutRef.current = setTimeout(() => {
          //  handleClose();
          // }, 1500);
        } catch (err) {
          setError(true);
          setReturnMessage("Failed to save configuration.");
          console.error("Save error:", err);
        }
        setSubmitting(false);
      }, 100);
    },
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue, setFieldTouched, setErrors, setTouched, resetForm } =
    formik;

  // ----------------------------------------------------------------------
  // Cross-validation logic
  // ----------------------------------------------------------------------
  useEffect(() => {
    const lActualMin = parseFloat(values.lActualMin);
    const lActualMax = parseFloat(values.lActualMax);
    const lForecastMin = parseFloat(values.lForecastMin);
    const lForecastMax = parseFloat(values.lForecastMax);
    const lPreviousMin = parseFloat(values.lPreviousMin);
    const lPreviousMax = parseFloat(values.lPreviousMax);

    // Check Actual Min vs Max
    if (values.bActual === true && !Number.isNaN(lActualMin) && !Number.isNaN(lActualMax)) {
      if (lActualMin >= lActualMax) {
        setError(true);
        setReturnMessage(
          <>
            <b>Actual value minimum</b> cannot be greater or equal to <b>Actual value maximum</b>!
          </>
        );
        return;
      }
    }

    // Check Forecast Min vs Max
    if (values.bForecast === true && !Number.isNaN(lForecastMin) && !Number.isNaN(lForecastMax)) {
      if (lForecastMin >= lForecastMax) {
        setError(true);
        setReturnMessage(
          <>
            <b>Forecast value minimum</b> cannot be greater or equal to <b>Forecast value maximum</b>!
          </>
        );
        return;
      }
    }

    // Check Previous Min vs Max
    if (values.bPrevious === true && !Number.isNaN(lPreviousMin) && !Number.isNaN(lPreviousMax)) {
      if (lPreviousMin >= lPreviousMax) {
        setError(true);
        setReturnMessage(
          <>
            <b>Previous value minimum</b> cannot be greater or equal to <b>Previous value maximum</b>!
          </>
        );
        return;
      }
    }

    // If all validations pass, clear error state
    setError(false);
    setReturnMessage("");
  }, [
    values.bActual,
    values.lActualMin,
    values.lActualMax,
    values.bForecast,
    values.lForecastMin,
    values.lForecastMax,
    values.bPrevious,
    values.lPreviousMin,
    values.lPreviousMax,
  ]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };

  // ----------------------------------------------------------------------
  // Validation Functions
  // ----------------------------------------------------------------------
  const validateFilter = (filter) => {
    const errors = {};
    const limits = DEFAULT_LIMITS.strFilterString;

    // Check for empty filterString (required field)
    if (!limits.emptyAllowed && (!filter.filterString || filter.filterString.trim() === "")) {
      errors.filterString = "Field cannot be empty.";
    }
    // Check for invalid characters in filterString
    else if (filter.filterString && limits.disallowedPattern && limits.disallowedPattern.test(filter.filterString)) {
      const invalidChars = filter.filterString.match(new RegExp(limits.disallowedPattern, "g"));
      const uniqueInvalidChars = [...new Set(invalidChars)];
      errors.filterString = `Invalid characters detected: ${uniqueInvalidChars.join(", ")}`;
    }
    // Check for length limit in filterString
    else if (filter.filterString && limits.maxLength && filter.filterString.length > limits.maxLength) {
      errors.filterString = `Text too long. Maximum ${limits.maxLength} characters allowed.`;
    }

    return errors;
  };

  const getFilterValidationErrors = () => {
    const allErrors = {};
    titleFilters.forEach((filter) => {
      const errors = validateFilter(filter);
      if (Object.keys(errors).length > 0) {
        allErrors[filter.id] = errors;
      }
    });
    return allErrors;
  };

  // Create emulated formik structure for SmartParameter error display
  const createFilterFormikStructure = () => {
    const filterErrors = getFilterValidationErrors();
    const emulatedErrors = {};
    const emulatedTouched = {};

    // Create field names and populate errors/touched for each filter
    titleFilters.forEach((filter) => {
      const filterStringField = `filter_${filter.id}_filterString`;

      // Set touched to true (since user has interacted with these fields)
      emulatedTouched[filterStringField] = true;

      // Set errors if they exist
      if (filterErrors[filter.id]?.filterString) {
        emulatedErrors[filterStringField] = filterErrors[filter.id].filterString;
      }
    });

    return { errors: emulatedErrors, touched: emulatedTouched };
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  let saveAndCloseDisabled = false;
  const filterErrors = getFilterValidationErrors();
  const hasFilterErrors = Object.keys(filterErrors).length > 0;
  const filterFormikStructure = createFilterFormikStructure();

  const hasValidationErrors = () => Object.keys(formik.errors).length > 0;
  if (hasValidationErrors() || hasFilterErrors) {
    saveAndCloseDisabled = true;
  }

  // Disable save button if cross-validation fails
  if (error) {
    saveAndCloseDisabled = true;
  }

  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <>
      <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
        Configure news
      </B1Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pl: 2,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">Configure news</Typography>

              <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
              </IconButton>
            </DialogTitle>

            <FormikProvider value={formik}>
              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                  px: 2,
                  pt: 2,
                  pb: 1,
                  width: "100%",
                  boxSizing: "border-box",
                  overflowY: "scroll",
                  overflowX: "hidden",
                  // Custom scrollbar styling
                  "&::-webkit-scrollbar": {
                    width: "16px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#e0e0e0",
                    borderRadius: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888888",
                    borderRadius: "6px",
                    border: "2px solid #e0e0e0",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#666666",
                  },
                  // Firefox scrollbar styling
                  scrollbarWidth: "thin",
                  scrollbarColor: "#888888 #e0e0e0",
                }}
              >
                <Stack spacing={2}>
                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nSignalInUse */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal in use"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nSignalInUse"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nSignalInUse}
                        onChange={(event) => {
                          setFieldValue("nSignalInUse", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nSignalUsedFor */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal used for"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nSignalUsedFor"
                        touched={touched}
                        errors={errors}
                      />
                      <FormControl fullWidth>
                        <Select
                          {...getFieldProps("nSignalUsedFor")}
                          input={<ModalContentOutlinedInput />}
                          error={Boolean(touched.nSignalUsedFor && errors.nSignalUsedFor)}
                        >
                          <ModalContentMenuItem value="0">Entry</ModalContentMenuItem>
                          <ModalContentMenuItem value="1">Exit</ModalContentMenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nSignalVisualization */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal visualization"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nSignalVisualization"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nSignalVisualization}
                        onChange={(event) => {
                          setFieldValue("nSignalVisualization", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nDrawTable */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Draw table with upcomming events"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nDrawTable"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nDrawTable}
                        onChange={(event) => {
                          setFieldValue("nDrawTable", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nDrawChart */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Draw signal lines with name in chart"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nDrawChart"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nDrawChart}
                        onChange={(event) => {
                          setFieldValue("nDrawChart", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nDrawKeepInCache */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Cache size for kept signal lines"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nDrawKeepInCache"
                        touched={touched}
                        errors={errors}
                      />
                      <SmartNumberInput textFieldName="nDrawKeepInCache" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nSignalNegation */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal inversion"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nSignalNegation"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nSignalNegation}
                        onChange={(event) => {
                          setFieldValue("nSignalNegation", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nShiftInSec */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Event shift in seconds"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nShiftInSec"
                        touched={touched}
                        errors={errors}
                      />
                      <SmartNumberInput textFieldName="nShiftInSec" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nPreSignalTime */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Pre event signal time"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nPreSignalTime"
                        touched={touched}
                        errors={errors}
                      />
                      <SmartNumberInput textFieldName="nPreSignalTime" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nPostSignalTime */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Post event signal time"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nPostSignalTime"
                        touched={touched}
                        errors={errors}
                      />
                      <SmartNumberInput textFieldName="nPostSignalTime" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRegionAll */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for ALL regions"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nRegionAll"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nRegionAll}
                        onChange={(event) => {
                          setFieldValue("nRegionAll", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRegionAud */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for AUD region"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nRegionAud"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nRegionAud}
                        onChange={(event) => {
                          setFieldValue("nRegionAud", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRegionBrl */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for BRL region"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nRegionBrl"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nRegionBrl}
                        onChange={(event) => {
                          setFieldValue("nRegionBrl", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRegionCad */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for CAD region"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nRegionCad"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nRegionCad}
                        onChange={(event) => {
                          setFieldValue("nRegionCad", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRegionChf */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for CHF region"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nRegionChf"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nRegionChf}
                        onChange={(event) => {
                          setFieldValue("nRegionChf", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRegionCny */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for CNY region"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nRegionCny"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nRegionCny}
                        onChange={(event) => {
                          setFieldValue("nRegionCny", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRegionEur */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for EUR region"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nRegionEur"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nRegionEur}
                        onChange={(event) => {
                          setFieldValue("nRegionEur", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRegionGbp */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for GBP region"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nRegionGbp"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nRegionGbp}
                        onChange={(event) => {
                          setFieldValue("nRegionGbp", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRegionHkd */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for HKD region"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nRegionHkd"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nRegionHkd}
                        onChange={(event) => {
                          setFieldValue("nRegionHkd", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRegionInr */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for INR region"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nRegionInr"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nRegionInr}
                        onChange={(event) => {
                          setFieldValue("nRegionInr", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRegionJpy */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for JPY region"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nRegionJpy"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nRegionJpy}
                        onChange={(event) => {
                          setFieldValue("nRegionJpy", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRegionKrw */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for KRW region"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nRegionKrw"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nRegionKrw}
                        onChange={(event) => {
                          setFieldValue("nRegionKrw", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRegionMxn */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for MXN region"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nRegionMxn"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nRegionMxn}
                        onChange={(event) => {
                          setFieldValue("nRegionMxn", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRegionNok */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for NOK region"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nRegionNok"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nRegionNok}
                        onChange={(event) => {
                          setFieldValue("nRegionNok", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRegionNzd */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for NZD region"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nRegionNzd"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nRegionNzd}
                        onChange={(event) => {
                          setFieldValue("nRegionNzd", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRegionSek */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for SEK region"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nRegionSek"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nRegionSek}
                        onChange={(event) => {
                          setFieldValue("nRegionSek", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRegionSgd */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for SGD region"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nRegionSgd"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nRegionSgd}
                        onChange={(event) => {
                          setFieldValue("nRegionSgd", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRegionUsd */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for USD region"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nRegionUsd"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nRegionUsd}
                        onChange={(event) => {
                          setFieldValue("nRegionUsd", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nRegionZar */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for ZAR region"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nRegionZar"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nRegionZar}
                        onChange={(event) => {
                          setFieldValue("nRegionZar", event.target.checked);
                        }}
                      />
                    </Box>

                    <Box />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nImportanceUndefined */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for undefined importance"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nImportanceUndefined"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nImportanceUndefined}
                        onChange={(event) => {
                          setFieldValue("nImportanceUndefined", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nImportanceLow */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for low importance"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nImportanceLow"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nImportanceLow}
                        onChange={(event) => {
                          setFieldValue("nImportanceLow", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nImportanceModerate */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for moderate importance"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nImportanceModerate"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nImportanceModerate}
                        onChange={(event) => {
                          setFieldValue("nImportanceModerate", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nImportanceHigh */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for high importance"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nImportanceHigh"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nImportanceHigh}
                        onChange={(event) => {
                          setFieldValue("nImportanceHigh", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nImpactUndefined */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for undefined impact"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nImpactUndefined"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nImpactUndefined}
                        onChange={(event) => {
                          setFieldValue("nImpactUndefined", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nImpactPositive */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for positive impact"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nImpactPositive"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nImpactPositive}
                        onChange={(event) => {
                          setFieldValue("nImpactPositive", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nImpactNegative */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signals for negative impact"
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="nImpactNegative"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nImpactNegative}
                        onChange={(event) => {
                          setFieldValue("nImpactNegative", event.target.checked);
                        }}
                      />
                    </Box>

                    <Box />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* bActual */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Actual value validation"
                      helpReference="HandleNews"
                      helpText=""
                      helpLink=""
                      fieldName="lActual"
                      touched={touched}
                      errors={errors}
                    />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.bActual === true}
                        onChange={(event) => {
                          setFieldValue("bActual", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  {values.bActual === true ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 2,
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* lActualMin */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="Actual value minimum"
                          helpReference="HandleNews"
                          helpText=""
                          helpLink=""
                          fieldName="lActualMin"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="lActualMin" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* lActualMax */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="Actual value maximum"
                          helpReference="HandleNews"
                          helpText=""
                          helpLink=""
                          fieldName="lActualMax"
                          touched={touched}
                          errors={errors}
                        />

                        <SmartNumberInput textFieldName="lActualMax" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* bForecast */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Forecast value validation"
                      helpReference="HandleNews"
                      helpText=""
                      helpLink=""
                      fieldName="lForecast"
                      touched={touched}
                      errors={errors}
                    />

                    <ModalContentSwitch
                      sx={{ mt: "12px" }}
                      checked={values.bForecast === true}
                      onChange={(event) => {
                        setFieldValue("bForecast", event.target.checked);
                      }}
                    />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  {values.bForecast === true ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 2,
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* lForecastMin */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="Forecast value minimum"
                          helpReference="HandleNews"
                          helpText=""
                          helpLink=""
                          fieldName="lForecastMin"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="lForecastMin" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* lForecastMax */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="Forecast value maximum"
                          helpReference="HandleNews"
                          helpText=""
                          helpLink=""
                          fieldName="lForecastMax"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="lForecastMax" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* bPrevious */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Previous value validation"
                      helpReference="HandleNews"
                      helpText=""
                      helpLink=""
                      fieldName="lPrevious"
                      touched={touched}
                      errors={errors}
                    />
                    <ModalContentSwitch
                      sx={{ mt: "12px" }}
                      checked={values.bPrevious === true}
                      onChange={(event) => {
                        setFieldValue("bPrevious", event.target.checked);
                      }}
                    />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  {values.bPrevious === true ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 2,
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* lPreviousMin */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="Previous value minimum"
                          helpReference="HandleNews"
                          helpText=""
                          helpLink=""
                          fieldName="lPreviousMin"
                          touched={touched}
                          errors={errors}
                        />
                        {values.bPrevious === true ? (
                          <SmartNumberInput textFieldName="lPreviousMin" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        ) : (
                          <Box sx={{ mt: "12px", height: "40px" }} />
                        )}
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* lPreviousMax */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="Previous value maximum"
                          helpReference="HandleNews"
                          helpText=""
                          helpLink=""
                          fieldName="lPreviousMax"
                          touched={touched}
                          errors={errors}
                        />
                        {values.bPrevious === true ? (
                          <SmartNumberInput textFieldName="lPreviousMax" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        ) : (
                          <Box sx={{ mt: "12px", height: "40px" }} />
                        )}
                      </Box>
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Value Relations */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Box sx={{ mb: 1 }}>
                      <SmartParameter
                        value={`Value relations (${valueRelations.length}/${MAX_VALUE_RELATIONS})`}
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="Value relations"
                      />
                    </Box>
                    <Stack spacing={2}>
                      {valueRelations.length < MAX_VALUE_RELATIONS && (
                        <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={addNewRelation}>
                          Add new value relation
                        </B1Tiny>
                      )}

                      {valueRelations.map((relation) => (
                        <Box
                          key={relation.id}
                          sx={{
                            backgroundColor: "white",
                            borderRadius: "10px",
                            p: 2,
                            boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                          }}
                        >
                          <Stack spacing={2}>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 2fr",
                                gap: 2,
                              }}
                            >
                              <Box>
                                <SmartParameter value="Remove relation" helpReference="HandleNews" helpText="" helpLink="" fieldName="Remove relation"/>
                                <IconButton sx={{ mt: "9px" }} size="small" onClick={() => removeRelation(relation.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              <Box>
                                <SmartParameter value="Relation condition" helpReference="HandleNews" helpText="" helpLink="" fieldName="Relation condition"/>
                                <FormControl fullWidth>
                                  <Select
                                    value={relation.relationCondition.toString()}
                                    onChange={(e) => updateRelation(relation.id, "relationCondition", e.target.value.toString())}
                                    input={<ModalContentOutlinedInput />}
                                    size="small"
                                  >
                                    {RELATION_CONDITION_OPTIONS.map((option) => (
                                      <ModalContentMenuItem key={option.value} value={option.value}>
                                        <Typography variant="contentSmall">{option.label}</Typography>
                                      </ModalContentMenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                            </Box>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Title Filters */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Box sx={{ mb: 1 }}>
                      <SmartParameter
                        value={`Title filters (${titleFilters.length}/${MAX_TITLE_FILTERS})`}
                        helpReference="HandleNews"
                        helpText=""
                        helpLink=""
                        fieldName="Title filters"
                      />
                    </Box>
                    <Stack spacing={2}>
                      {titleFilters.length < MAX_TITLE_FILTERS && (
                        <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={addNewFilter}>
                          Add new title filter
                        </B1Tiny>
                      )}

                      {titleFilters.map((filter) => (
                        <Box
                          key={filter.id}
                          sx={{
                            backgroundColor: "white",
                            borderRadius: "10px",
                            p: 2,
                            boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                          }}
                        >
                          <Stack spacing={2}>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr 1fr",
                                gap: 2,
                              }}
                            >
                              <Box>
                                <SmartParameter value="Remove filter" helpReference="HandleNews" helpText="" helpLink="" fieldName="Remove filter"/>
                                <IconButton sx={{ mt: "9px" }} size="small" onClick={() => removeFilter(filter.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>

                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 2fr",
                                gap: 2,
                              }}
                            >
                              <Box>
                                <SmartParameter value="Filter type" helpReference="HandleNews" helpText="" helpLink="" fieldName="Filter type"/>
                                <FormControl fullWidth>
                                  <Select
                                    value={filter.filterType.toString()}
                                    onChange={(e) => updateFilter(filter.id, "filterType", e.target.value.toString())}
                                    input={<ModalContentOutlinedInput />}
                                    size="small"
                                  >
                                    {FILTER_TYPE_OPTIONS.map((option) => (
                                      <ModalContentMenuItem key={option.value} value={option.value}>
                                        <Typography variant="contentSmall">{option.label}</Typography>
                                      </ModalContentMenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                              <Box>
                                <SmartParameter
                                  value="Title name to filter"
                                  helpReference="HandleNews"
                                  helpText=""
                                  helpLink=""
                                  fieldName={`filter_${filter.id}_filterString`}
                                  touched={filterFormikStructure.touched}
                                  errors={filterFormikStructure.errors}
                                />
                                <ModalContentTextField
                                  size="small"
                                  fullWidth
                                  value={filter.filterString}
                                  onChange={(e) => updateFilter(filter.id, "filterString", e.target.value)}
                                  error={Boolean(filterErrors[filter.id]?.filterString)}
                                />
                              </Box>
                            </Box>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </DialogContent>

              <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
                <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
              </Box>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 2,
                  pt: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleReset}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Reset
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    disabled={saveAndCloseDisabled}
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Save
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleClose}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Close
                  </ModalActionsLoadingButton>
                </Box>
              </DialogActions>
            </FormikProvider>
          </>
        }
      />
    </>
  );
});
